import {
  ProfessionalAuditOutcome,
  ProfessionalAuditType
} from '@mieterengel/database-types/types/enums'

import { StructuredText } from '@app/lib/inputs/RichEditor/types'
import { LatestMessage } from '@app/queries/CustomerAsLawyer/models'

export enum MessagesActionType {
  ANSWER = 'LAWYER_CHAT/ANSWER',
  ANSWER_SUCCESS = 'LAWYER_CHAT/ANSWER_SUCCESS',
  ANSWER_ERROR = 'LAWYER_CHAT/ANSWER_ERROR',
  LAWYER_DOCUMENTS_ERROR = 'LAWYER_CHAT/SET_LAWYER_DOCUMENTS_ERROR',
  LAWYER_DOCUMENTS_SUCCESS = 'LAWYER_CHAT/LAWYER_DOCUMENTS_SUCCESS',
  LAWYER_DOC_UPLOAD = 'LAWYER_CHAT/LAWYER_DOCS_UPLOAD',
  LAWYER_DOC_UPLOAD_ERROR = 'LAWYER_CHAT/LAWYER_DOCS_UPLOAD_ERROR',
  SHOW_ERRORS = 'LAWYER_CHAT/SHOW_ERRORS',
  ADD_NOTES = 'LAWYER_CHAT/ADD_NOTES',
  ADD_SELECTED_DOC = 'LAWYER_CHAT/ADD_SELECTED_DOC',
  CLEAR_SELECTED_DOCS = 'LAWYER_CHAT/CLEAR_SELECTED_DOCS',
  REMOVE_SELECTED_DOC = 'LAWYER_CHAT/REMOVE_SELECTED_DOC',
  ADD_SELECTED_LETTER = 'LAWYER_CHAT/ADD_SELECTED_LETTER',
  REMOVE_SELECTED_LETTER = 'LAWYER_CHAT/REMOVE_SELECTED_LETTER',
  ADD_SELECTED_LETTER_TO_AUDIT = 'LAWYER_CHAT/ADD_SELECTED_LETTER_TO_AUDIT',
  REMOVE_SELECTED_LETTER_FROM_AUDIT = 'LAWYER_CHAT/REMOVE_SELECTED_LETTER_FROM_AUDIT',
  ADD_AUDIT_SELECTED_DOC = 'LAWYER_CHAT/AUDIT/ADD_SELECTED_DOC',
  REMOVE_AUDIT_SELECTED_DOC = 'LAWYER_CHAT/AUDIT/REMOVE_SELECTED_DOC',
  SAVE_AUDIT_DATA = 'LAWYER_CHAT/AUDIT/SAVE_AUDIT_DATA',
  SUBMIT_AUDIT_REPORT = 'LAWYER_CHAT/AUDIT/SUBMIT_AUDIT_REPORT',
  SAVE_OPEN_LETTER_REQUEST_ID = 'LAWYER_CHAT/SAVE_OPEN_LETTER_REQUEST_ID'
}

export default class LawyerChatAction {
  static lawyerAnswer(meta: {
    data: { message: string }
    latestMessageId: number
    isInformation?: boolean
    templateId?: number
    customerId: number
  }) {
    return {
      type: MessagesActionType.ANSWER as const,
      payload: {
        sending: true
      },
      meta
    }
  }

  static lawyerAnswerSuccess(chat) {
    return {
      type: MessagesActionType.ANSWER_SUCCESS as const,
      payload: {
        sending: false,
        answerError: null,
        chat
      }
    }
  }

  static lawyerAnswerError(e) {
    return {
      type: MessagesActionType.ANSWER_ERROR as const,
      payload: {
        sending: false,
        answerError: e
      }
    }
  }

  static onUploadDoc(data, audit, letter = false) {
    return {
      type: MessagesActionType.LAWYER_DOC_UPLOAD as const,
      payload: {
        sending: true
      },
      meta: {
        audit,
        data,
        letter
      }
    }
  }

  static onUploadDocError(e) {
    return {
      type: MessagesActionType.LAWYER_DOC_UPLOAD_ERROR as const,
      payload: {
        sending: false,
        lawyerDocUploadError: e
      }
    }
  }

  static lawyerDocumentsError(e) {
    return {
      type: MessagesActionType.LAWYER_DOCUMENTS_ERROR as const,
      payload: {
        sending: false,
        documentsError: e
      }
    }
  }

  static setLawyerDocuments(data) {
    return {
      type: MessagesActionType.LAWYER_DOCUMENTS_SUCCESS as const,
      payload: {
        documents: data,
        sending: false,
        documentsError: null
      }
    }
  }

  static showErrors(showTextAreaError) {
    return {
      type: MessagesActionType.SHOW_ERRORS as const,
      payload: {
        showTextAreaError
      }
    }
  }

  static addNotes(notes) {
    return {
      type: MessagesActionType.ADD_NOTES as const,
      payload: {
        notes
      }
    }
  }

  static onSelectedDocAdd(id) {
    return {
      type: MessagesActionType.ADD_SELECTED_DOC as const,
      payload: {
        id
      }
    }
  }

  static onSelectedDocRemove(id) {
    return {
      type: MessagesActionType.REMOVE_SELECTED_DOC as const,
      payload: {
        id
      }
    }
  }

  static clearSelectedDocs() {
    return {
      type: MessagesActionType.CLEAR_SELECTED_DOCS
    }
  }

  static onAddSelectedLetter(id) {
    return {
      type: MessagesActionType.ADD_SELECTED_LETTER as const,
      payload: {
        id
      }
    }
  }

  static onRemoveSelectedLetter(id) {
    return {
      type: MessagesActionType.REMOVE_SELECTED_LETTER as const,
      payload: {
        id
      }
    }
  }

  static onAddSelectedLetterToAudit(id) {
    return {
      type: MessagesActionType.ADD_SELECTED_LETTER_TO_AUDIT as const,
      payload: {
        id
      }
    }
  }

  static onRemoveSelectedLetterFromAudit(id) {
    return {
      type: MessagesActionType.REMOVE_SELECTED_LETTER_FROM_AUDIT as const,
      payload: {
        id
      }
    }
  }

  static onAuditSelectedDocAdd(id) {
    return {
      type: MessagesActionType.ADD_AUDIT_SELECTED_DOC as const,
      payload: {
        id
      }
    }
  }

  static onAuditSelectedDocRemove(id) {
    return {
      type: MessagesActionType.REMOVE_AUDIT_SELECTED_DOC as const,
      payload: {
        id
      }
    }
  }

  static saveAuditData(data) {
    return {
      type: MessagesActionType.SAVE_AUDIT_DATA as const,
      payload: {
        data
      }
    }
  }

  static submitAuditReport(meta: {
    data: {
      outcome?: ProfessionalAuditOutcome
      findings: StructuredText
      recommendations: StructuredText
      auditType: ProfessionalAuditType
    }
    latestMessage: LatestMessage
    templateId?: number
    customerId: number
  }) {
    return {
      type: MessagesActionType.SUBMIT_AUDIT_REPORT as const,
      payload: {
        sending: true
      },
      meta
    }
  }

  static saveOpenLetterRequestId(letterRequestId) {
    return {
      type: MessagesActionType.SAVE_OPEN_LETTER_REQUEST_ID as const,
      payload: {
        letterRequestId
      }
    }
  }
}

export type AnswerAction = ReturnType<typeof LawyerChatAction['lawyerAnswer']>
export type SubmitAuditReportAction = ReturnType<typeof LawyerChatAction['submitAuditReport']>
export type UploadDocumentAction = ReturnType<typeof LawyerChatAction['onUploadDoc']>
